import { SelectionProviderChildProps } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { Messages } from '../../../gen-src/Localize';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { ColumnsGridSortProvider } from '../sorting/sortProviders/ColumnsGridSortProvider';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { VirtualConnectionTableColumnsQueryGrid } from './VirtualConnectionTableColumnsQueryGrid';
import { VirtualConnectionTableLineageCounts } from './VirtualConnectionTableCountsQueryWrapper';
import { VirtualConnectionTableDownstreamDashboardsQueryGrid } from './VirtualConnectionTableDownstreamDashboardsQueryGrid';
import { VirtualConnectionTableDownstreamDatasourcesQueryGrid } from './VirtualConnectionTableDownstreamDatasourcesQueryGrid';
import { VirtualConnectionTableDownstreamFlowsQueryGrid } from './VirtualConnectionTableDownstreamFlowsQueryGrid';
import { VirtualConnectionTableDownstreamMetricsQueryGrid } from './VirtualConnectionTableDownstreamMetricsQueryGrid';
import { VirtualConnectionTableDownstreamOwnersPane } from './VirtualConnectionTableDownstreamOwnersPane';
import { VirtualConnectionTableDownstreamSheetsQueryGrid } from './VirtualConnectionTableDownstreamSheetsQueryGrid';
import { VirtualConnectionTableDownstreamWorkbooksQueryGrid } from './VirtualConnectionTableDownstreamWorkbooksQueryGrid';
import { VirtualConnectionTableUpstreamDatabasesQueryGrid } from './VirtualConnectionTableUpstreamDatabasesQueryGrid';
import { VirtualConnectionTableUpstreamTablesQueryGrid } from './VirtualConnectionTableUpstreamTablesQueryGrid';
import { VirtualConnectionTableUpstreamVirtualConnectionsQueryGrid } from './VirtualConnectionTableUpstreamVirtualConnectionsQueryGrid';

export type VirtualConnectionTableLineageItemConfig = LineageItemConfig<VirtualConnectionTableLineageCounts, VirtualConnectionTableLineagePaneParams>;
export type VirtualConnectionTableLineageSection = LineageSection<VirtualConnectionTableLineageCounts, VirtualConnectionTableLineagePaneParams>;

export type VirtualConnectionTableLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly vizportalUrlId: string;
  readonly filteredColumnId: string | null;
  readonly count: number | null;
};

const VirtualConnectionTableUpstreamItems: ReadonlyArray<VirtualConnectionTableLineageItemConfig> = [
  {
    lineageItemType: LineageItemType.Database,
    messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
    messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
    count: counts => counts.upstreamDatabases,
    path: (urls, id) => urls.virtualConnectionTableLineageUpstreamDatabasesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DatabasesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <VirtualConnectionTableUpstreamDatabasesQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </DatabasesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Table,
    messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
    messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
    count: counts => counts.upstreamTables,
    path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageUpstreamTablesPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <TablesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <VirtualConnectionTableUpstreamTablesQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </TablesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.VirtualConnection,
    messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
    messagesForPane: { withCount: Messages.UpstreamVirtualConnectionsWithCount, withoutCount: Messages.UpstreamVirtualConnections },
    count: counts => counts.upstreamVirtualConnections,
    path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageUpstreamVirtualConnectionsPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <VirtualConnectionsGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <VirtualConnectionTableUpstreamVirtualConnectionsQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              filteredColumnId={paneParams.filteredColumnId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </VirtualConnectionsGridSortProvider>
      </PaneWithTitle>
    ),
  },
];

const VirtualConnectionTableAnchor: VirtualConnectionTableLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnectionTable,
  messagesForLineage: { withCount: Messages.ColumnsWithCount, withoutCount: Messages.Columns },
  messagesForPane: { withCount: Messages.ColumnsWithCount, withoutCount: Messages.Columns },
  count: counts => counts.columns,
  path: (urls, id) => urls.virtualConnectionTableLineageFieldsPath(id),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <ColumnsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <VirtualConnectionTableColumnsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            selectedKeys={paneParams.selectedKeys}
            updateSelectionForKey={paneParams.updateSelectionForKey}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </ColumnsGridSortProvider>
    </PaneWithTitle>
  ),
};

function getVirtualConnectionTableDownstreamItems(): ReadonlyArray<VirtualConnectionTableLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.DownstreamFlowsWithCount, withoutCount: Messages.DownstreamFlows },
      count: counts => counts.downstreamFlows,
      path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageDownstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionTableDownstreamFlowsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.DownstreamDatasourcesWithCount, withoutCount: Messages.DownstreamDatasources },
      count: counts => counts.downstreamDatasources,
      path: (urls, id) => urls.virtualConnectionTableLineageDownstreamDatasourcesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionTableDownstreamDatasourcesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Workbook,
      messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
      messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
      count: counts => counts.downstreamWorkbooks,
      path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageDownstreamWorkbooksPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <WorkbooksGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionTableDownstreamWorkbooksQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </WorkbooksGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageDownstreamSheetsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionTableDownstreamSheetsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageDownstreamDashboardsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionTableDownstreamDashboardsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Metric,
      messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
      messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
      count: counts => counts.downstreamMetrics,
      path: (urls, vizportalUrlId) => urls.virtualConnectionTableLineageDownstreamMetricsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <MetricsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionTableDownstreamMetricsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredColumnId={paneParams.filteredColumnId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </MetricsGridSortProvider>
        </PaneWithTitle>
      ),
    }
  ];
}
const memoizedGetVirtualConnectionTableDownstreamItems = defaultMemoize(getVirtualConnectionTableDownstreamItems);

const VirtualConnectionTableOwners: VirtualConnectionTableLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, id) => urls.virtualConnectionTableLineageDownstreamOwnersPath(id),
  paneFactory: (paneParams) => (
    <VirtualConnectionTableDownstreamOwnersPane
      title={paneParams.title}
      vizportalUrlId={paneParams.vizportalUrlId}
      filteredColumnId={paneParams.filteredColumnId}
      rowCount={paneParams.count}
    />
  ),
};

function getVirtualConnectionTableLineageConfiguration(): ReadonlyArray<VirtualConnectionTableLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: VirtualConnectionTableUpstreamItems,
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [VirtualConnectionTableAnchor],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetVirtualConnectionTableDownstreamItems(),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [VirtualConnectionTableOwners],
    },
  ];
}
export const memoizedGetVirtualConnectionTableLineageConfiguration = defaultMemoize(getVirtualConnectionTableLineageConfiguration);
